import { useState, useRef, useEffect, useCallback } from "react";
import { officialFeesApi, type OfficeSuggestion } from "../api/officialFees";

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSelect: (suggestion: OfficeSuggestion) => void;
  placeholder?: string;
  hasError?: boolean;
}

export function OfficeAutocomplete({ value, onChange, onSelect, placeholder, hasError }: Props) {
  const [suggestions, setSuggestions] = useState<OfficeSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchSuggestions = useCallback((q: string) => {
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    officialFeesApi.officeSuggestions(q).then((data) => {
      setSuggestions(data);
      setOpen(data.length > 0);
      setActiveIndex(-1);
    });
  }, []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const q = e.target.value;
    onChange(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(q), 250);
  }

  function handleSelect(s: OfficeSuggestion) {
    onSelect(s);
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  // Close dropdown on outside click
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        className={[
          "w-full rounded border px-3 py-2 text-sm bg-[var(--color-bg-input)] text-[var(--color-text-primary)] outline-none",
          "focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] transition-colors",
          hasError
            ? "border-red-500"
            : "border-[var(--color-border)] hover:border-[var(--color-border-hover)]",
        ].join(" ")}
      />
      {open && (
        <ul className="absolute z-50 mt-1 w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] shadow-lg max-h-64 overflow-y-auto">
          {suggestions.map((s, i) => (
            <li
              key={s.office_name}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(s);
              }}
              className={[
                "px-3 py-2 cursor-pointer text-sm",
                i === activeIndex
                  ? "bg-[var(--color-primary)] text-white"
                  : "text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
              ].join(" ")}
            >
              <div className="font-medium truncate">{s.office_name}</div>
              <div
                className={[
                  "text-xs truncate font-mono",
                  i === activeIndex ? "text-white/80" : "text-[var(--color-text-secondary)]",
                ].join(" ")}
              >
                {s.office_account_number}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
